// counsellorUtils.ts

// Define a simplified version of Counsellor for status checks
interface CounsellorStatus {
  isArchived?: boolean;
  applicationStatus?: string;
  status?: string;
}

export type CounsellorJourneyStatus =
  | 'Unknown'
  | 'Archived'
  | 'Suspended'
  | 'Rejected'
  | 'Declined'
  | 'Application Expired'
  | 'Offboarded'
  | 'Registered Interest'
  | 'Application Issued'
  | 'Application Not Started'
  | 'Application in Progress'
  | 'Application Under Review'
  | 'Interview Scheduled'
  | 'Interview Completed'
  | 'Awaiting Legal Resubmission'
  | 'Legal Info Requested'
  | 'Legal Info Submitted'
  | 'Onboarding Review'
  | 'Pending Agreement'
  | 'Pending Self-Completion'
  | 'Ready for Onboarding'
  | 'Active Counsellor';

type StatusStyle = {
  backgroundColor: string;
  color: string;
};

export const getCounsellorStatus = (counsellor: CounsellorStatus): CounsellorJourneyStatus => {
  if (!counsellor) return 'Unknown';
  if (counsellor.isArchived) return 'Archived';

  // Check for suspended status
  if (counsellor.applicationStatus === 'Suspended') {
    return 'Suspended';
  }

  // Check for rejected status
  if (counsellor.applicationStatus === 'Rejected') {
    return 'Rejected';
  }

  // Check for active status
  if (counsellor.applicationStatus === 'Active') {
    return 'Active Counsellor';
  }

  // Main status flow - canonical states (docs/Application Status.md) with
  // legacy spellings mapped to their canonical display.
  switch (counsellor.applicationStatus) {
    case 'Registered Interest':
      return 'Registered Interest';
    case 'Pending': // legacy: registered interest or issued-not-started
      return 'Registered Interest';
    case 'Application Issued':
      return 'Application Issued';
    case 'Application In Progress':
    case 'Started':
      return 'Application in Progress';
    case 'Application Submitted':
    case 'Submitted':
      return 'Application Under Review';
    case 'Interview Scheduled':
      return 'Interview Scheduled';
    case 'Interview Completed':
      return 'Interview Completed';
    case 'Declined':
      return 'Declined';
    case 'Application Expired':
      return 'Application Expired';
    case 'Offboarded':
      return 'Offboarded';
    case 'Legal Information Requested':
    case 'Legal Info Requested':
      return 'Legal Info Requested';
    case 'Legal Info Update Requested':
    case 'Legal Info Rejected':
      return 'Awaiting Legal Resubmission';
    case 'Legal Information Submitted':
    case 'Legal Info Submitted':
      return 'Legal Info Submitted';
    case 'Onboarding Review':
      return 'Onboarding Review';
    case 'Pending Agreement':
    case 'Legal Info Approved':
      return 'Pending Agreement';
    case 'Pending Self-Completion':
      return 'Pending Self-Completion';
    case 'Ready For Onboarding':
      // Legacy value was (mis)used for rejected legal info - show it as
      // awaiting resubmission rather than looking ready for onboarding.
      return 'Awaiting Legal Resubmission';
    case 'Accepted':
      return 'Ready for Onboarding';
    default:
      return 'Application Not Started';
  }
};

export const getStatusChipStyles = (counsellor: CounsellorStatus): StatusStyle => {
  if (counsellor.isArchived) {
    return {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    };
  }

  const statusStyles: Record<CounsellorJourneyStatus, StatusStyle> = {
    'Unknown': {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    'Archived': {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    'Suspended': {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    'Rejected': {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    'Declined': {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    'Application Expired': {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    'Offboarded': {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    'Registered Interest': {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    'Application Issued': {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    'Application Not Started': {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    'Application in Progress': {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    'Application Under Review': {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    'Interview Scheduled': {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    'Interview Completed': {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    'Pending Agreement': {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    'Awaiting Legal Resubmission': {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    'Legal Info Requested': {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    'Legal Info Submitted': {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    'Onboarding Review': {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    'Pending Self-Completion': {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
    },
    'Ready for Onboarding': {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    'Active Counsellor': {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    }
  };

  const status = getCounsellorStatus(counsellor);
  return statusStyles[status];
};